import { createSlice } from '@reduxjs/toolkit';
import { getPersistedDjSidebarWidth } from '../utils/djSidebarWidth';

// Initial state with defaults - persisted state will be loaded after mount
const initialState = {
    sidebarOpen: false,
    sidebarWidth: getPersistedDjSidebarWidth(),
    syncStatus: 'not_synced', // 'not_synced' | 'checking' | 'synced' | 'partial' (import stopped/failed, whole playlists kept) | 'error'
    rekordboxPath: null,
    rekordboxExists: null,
    rekordboxPlaylists: [], // Playlists from last import
    lastSyncTime: null,
    importing: false,
    exporting: false,
    error: null,
    persistedStateLoaded: false
};

const rekordboxSlice = createSlice({
    name: 'rekordbox',
    initialState,
    reducers: {
        loadPersistedRekordboxState(state, action) {
            // Only load once
            if (state.persistedStateLoaded) return;

            // If payload provided (from async load), use it
            if (action.payload) {
                const { lastSyncTime, rekordboxPath, rekordboxPlaylists } = action.payload;
                if (lastSyncTime) {
                    state.lastSyncTime = lastSyncTime;
                    state.syncStatus = 'synced';
                }
                if (rekordboxPath) {
                    state.rekordboxPath = rekordboxPath;
                }
                if (rekordboxPlaylists && rekordboxPlaylists.length > 0) {
                    state.rekordboxPlaylists = rekordboxPlaylists;
                }
            }

            state.persistedStateLoaded = true;
        },
        toggleRekordboxSidebar(state) {
            state.sidebarOpen = !state.sidebarOpen;
        },
        setRekordboxSidebarOpen(state, action) {
            state.sidebarOpen = action.payload;
        },
        setRekordboxSidebarWidth(state, action) {
            state.sidebarWidth = action.payload;
        },
        setSyncStatus(state, action) {
            state.syncStatus = action.payload;
        },
        setRekordboxPath(state, action) {
            state.rekordboxPath = action.payload;
        },
        setRekordboxExists(state, action) {
            state.rekordboxExists = action.payload;
        },
        setRekordboxPlaylists(state, action) {
            state.rekordboxPlaylists = action.payload;
        },
        setLastSyncTime(state, action) {
            state.lastSyncTime = action.payload;
        },
        setImporting(state, action) {
            state.importing = action.payload;
        },
        setExporting(state, action) {
            state.exporting = action.payload;
        },
        setRekordboxError(state, action) {
            state.error = action.payload;
        },
        // Owner-scoped "Last synced" from the main process (djlibrary:getSyncState),
        // fetched on boot and on every account change by DjSyncStateHydrator.
        // Null = this account never synced this app: leave the state alone.
        hydrateRekordboxSyncState(state, action) {
            const at = action.payload;
            if (!at) return;
            state.lastSyncTime = at;
            if (state.syncStatus === 'not_synced') state.syncStatus = 'synced';
        },
        resetRekordboxState(state) {
            state.syncStatus = 'not_synced';
            state.rekordboxPlaylists = [];
            state.lastSyncTime = null;
            state.error = null;
        }
    },
});

export const {
    loadPersistedRekordboxState,
    toggleRekordboxSidebar,
    setRekordboxSidebarOpen,
    setRekordboxSidebarWidth,
    setSyncStatus,
    setRekordboxPath,
    setRekordboxExists,
    setRekordboxPlaylists,
    setLastSyncTime,
    setImporting,
    setExporting,
    setRekordboxError,
    hydrateRekordboxSyncState,
    resetRekordboxState
} = rekordboxSlice.actions;

export default rekordboxSlice.reducer;
